// components/lead-generation/business-types/ESGComplianceFilter.js

import React, { useState } from 'react';
import { ESG_COMPLIANCE_CONFIG } from '../../../config/business-types/esg-compliance.config.js';

export default function ESGComplianceFilter({ onFilterChange, currentFilters = {} }) {
  const [filters, setFilters] = useState({
    complianceType: currentFilters.complianceType || 'all',
    industryRisk: currentFilters.industryRisk || 'all',
    reportingDeadline: currentFilters.reportingDeadline || 'all',
    country: currentFilters.country || 'all',
    companySize: currentFilters.companySize || 'all'
  });

  const updateFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearAllFilters = () => {
    const clearedFilters = ESG_COMPLIANCE_CONFIG.defaultFilters;
    setFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const complianceOptions = [
    { value: 'all', label: 'All Companies', count: null },
    { value: 'csrd_mandatory', label: 'CSRD Mandatory (2024-2028)', count: null, priority: 'HIGH' },
    { value: 'vsme_eligible', label: 'VSME Eligible SMEs', count: null, priority: 'MEDIUM' },
    { value: 'approaching_threshold', label: 'Approaching Thresholds', count: null, priority: 'MEDIUM' },
    { value: 'future_preparation', label: 'Future ESG Preparation', count: null, priority: 'LOW' }
  ];

  const riskOptions = [
    { value: 'all', label: 'All Industries', count: null },
    { value: 'high', label: 'High ESG Risk Industries', count: null, examples: 'Manufacturing, Mining, Energy' },
    { value: 'medium', label: 'Medium ESG Risk', count: null, examples: 'Transport, Construction, Finance' },
    { value: 'low', label: 'Low ESG Risk', count: null, examples: 'Services, Technology' }
  ];

  const deadlineOptions = [
    { value: 'all', label: 'All Deadlines', count: null },
    { value: '2024', label: '2024 (Immediate)', count: null, urgency: 'CRITICAL' },
    { value: '2025', label: '2025 (High Priority)', count: null, urgency: 'HIGH' },
    { value: '2026', label: '2026 (Listed SMEs)', count: null, urgency: 'MEDIUM' },
    { value: '2027-2028', label: '2027-2028 (Planning)', count: null, urgency: 'LOW' }
  ];

  const countryOptions = [
    { value: 'all', label: 'All Countries' },
    { value: 'uk', label: 'United Kingdom' },
    { value: 'germany', label: 'Germany' },
    { value: 'france', label: 'France' },
    { value: 'netherlands', label: 'Netherlands' },
    { value: 'ireland', label: 'Ireland' },
    { value: 'other_eu', label: 'Other EU Countries' }
  ];

  const sizeOptions = [
    { value: 'all', label: 'All Company Sizes' },
    { value: 'large', label: 'Large (500+ employees)' },
    { value: 'medium', label: 'Medium (50-250 employees)' },
    { value: 'small', label: 'Small (10-50 employees)' },
    { value: 'micro', label: 'Micro (<10 employees)' }
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">ESG Compliance Filters</h3>
            <p className="text-sm text-gray-600">Target VSME & CSRD requirements</p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-xs font-medium text-green-600">ESG Focused</span>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* ESG Compliance Type Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <i className="fas fa-leaf text-green-500 mr-2"></i>
            ESG Reporting Requirements
          </label>
          <div className="space-y-2">
            {complianceOptions.map((option) => (
              <label key={option.value} className="flex items-center space-x-3 cursor-pointer group">
                <input
                  type="radio"
                  value={option.value}
                  checked={filters.complianceType === option.value}
                  onChange={(e) => updateFilter('complianceType', e.target.value)}
                  className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-900 group-hover:text-gray-700">
                      {option.label}
                    </span>
                    {option.priority && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        option.priority === 'HIGH' 
                          ? 'bg-red-100 text-red-700' 
                          : option.priority === 'MEDIUM' 
                            ? 'bg-yellow-100 text-yellow-700' 
                            : 'bg-gray-100 text-gray-700'
                      }`}>
                        {option.priority}
                      </span>
                    )}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Industry Risk Level Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <i className="fas fa-industry text-blue-500 mr-2"></i>
            Industry ESG Risk Level
          </label>
          <div className="space-y-2">
            {riskOptions.map((option) => (
              <label key={option.value} className="flex items-center space-x-3 cursor-pointer group">
                <input
                  type="radio"
                  value={option.value}
                  checked={filters.industryRisk === option.value}
                  onChange={(e) => updateFilter('industryRisk', e.target.value)}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900 group-hover:text-gray-700">
                    {option.label}
                  </div>
                  {option.examples && (
                    <div className="text-xs text-gray-500 mt-1">
                      {option.examples}
                    </div>
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Reporting Deadline Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <i className="fas fa-calendar-alt text-purple-500 mr-2"></i>
            CSRD Reporting Deadline
          </label>
          <div className="space-y-2">
            {deadlineOptions.map((option) => (
              <label key={option.value} className="flex items-center space-x-3 cursor-pointer group">
                <input
                  type="radio"
                  value={option.value}
                  checked={filters.reportingDeadline === option.value}
                  onChange={(e) => updateFilter('reportingDeadline', e.target.value)}
                  className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                />
                <div className="flex-1 flex items-center justify-between">
                  <span className="text-sm text-gray-900 group-hover:text-gray-700">
                    {option.label}
                  </span>
                  {option.urgency && (
                    <div className={`flex items-center space-x-1 ${
                      option.urgency === 'CRITICAL' 
                        ? 'text-red-600' 
                        : option.urgency === 'HIGH' 
                          ? 'text-orange-600' 
                          : option.urgency === 'MEDIUM'
                            ? 'text-yellow-600'
                            : 'text-gray-600'
                    }`}>
                      <i className="fas fa-clock text-xs"></i>
                      <span className="text-xs font-medium">{option.urgency}</span>
                    </div>
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Country Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <i className="fas fa-globe-europe text-indigo-500 mr-2"></i>
            Country/Region
          </label>
          <select
            value={filters.country}
            onChange={(e) => updateFilter('country', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            {countryOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Company Size Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <i className="fas fa-users text-teal-500 mr-2"></i>
            Company Size
          </label>
          <select
            value={filters.companySize}
            onChange={(e) => updateFilter('companySize', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          >
            {sizeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Filter Actions */}
        <div className="pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <button
              onClick={clearAllFilters}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium"
            >
              <i className="fas fa-undo mr-2"></i>
              Clear All Filters
            </button>
            
            <div className="text-xs text-gray-500">
              <i className="fas fa-info-circle mr-1"></i>
              VSME: €2-50M turnover, 10-250 employees
            </div>
          </div>
        </div>

        {/* ESG Quick Tips */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <h4 className="font-medium text-green-900 text-sm mb-2">
            <i className="fas fa-lightbulb text-green-600 mr-2"></i>
            ESG Lead Generation Tips
          </h4>
          <ul className="text-xs text-green-700 space-y-1">
            <li>• Focus on CSRD mandatory companies for immediate opportunities</li>
            <li>• High-risk industries (manufacturing, energy) have urgent needs</li>
            <li>• VSME-eligible companies offer competitive advantage positioning</li>
            <li>• 2024-2025 deadlines create urgency for large companies</li>
          </ul>
        </div>
      </div>
    </div>
  );
}